import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Order } from '../../../store/order/api/interface';
import { ShippingAddress } from '../../../client/services';
import { toastHandler } from '../../../utils/toastHandler';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { customIcon } from '../../../utils/icon';
import { extractCoordinatesFromGoogleMapsUrl } from '../../../utils/googleMapsUrlParser';

interface AddressEditDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order | null | undefined;
  onAddressUpdate: (updatedOrder: Order) => void;
}

interface AddressData {
  addressName: string;
  buildingNumber: string;
  street: string;
  zone: string;
  latitude: number | null;
  longitude: number | null;
  addressType: string;
}

// Map click handler component
const MapClickHandler: React.FC<{ onLocationSelect: (lat: number, lng: number) => void }> = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      const { lat, lng } = e.latlng;
      onLocationSelect(lat, lng);
    },
  });
  return null;
};

const AddressEditDialog: React.FC<AddressEditDialogProps> = ({
  open,
  onClose,
  order,
  onAddressUpdate,
}) => {
  const [loading, setLoading] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('');
  const [urlError, setUrlError] = useState<string | null>(null);
  const [formData, setFormData] = useState<AddressData>({
    addressName: '',
    buildingNumber: '',
    street: '',
    zone: '',
    latitude: null,
    longitude: null,
    addressType: 'home',
  });

  // Initialize form data when dialog opens
  useEffect(() => {
    if (open && order) {
      const shippingAddress = order.shippingAddress as any || {};
      
      setFormData({
        addressName: shippingAddress.addressName || '',
        buildingNumber: shippingAddress.buildingNumber || '',
        street: shippingAddress.street || '',
        zone: shippingAddress.zone || '',
        latitude: shippingAddress.latitude || 25.3548,
        longitude: shippingAddress.longitude || 51.1839,
        addressType: shippingAddress.addressType || 'home',
      });
      setError(null);
    }
  }, [open, order]);

  const handleInputChange = (field: keyof AddressData, value: string) => {
    const numValue = field === 'latitude' || field === 'longitude' 
      ? (value === '' ? null : parseFloat(value))
      : value;
    setFormData(prev => ({
      ...prev,
      [field]: numValue,
    }));
  };

  const handleMapClick = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
    }));
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGoogleMapsUrl(event.target.value);
    setUrlError(null);
  };

  const handleExtractFromUrl = async () => {
    if (!googleMapsUrl.trim()) {
      setUrlError('Please enter a Google Maps URL');
      return;
    }

    setIsExtracting(true);
    setUrlError(null);

    try {
      const result = await extractCoordinatesFromGoogleMapsUrl(googleMapsUrl);
      
      if (result.success && result.latitude !== null && result.longitude !== null) {
        setFormData(prev => ({
          ...prev,
          latitude: result.latitude,
          longitude: result.longitude,
        }));
        setUrlError(null);
        
        const successMessage = `✅ Coordinates extracted: ${result.latitude}, ${result.longitude}`;
        toastHandler({ type: 'success', message: successMessage });
        
        setGoogleMapsUrl('');
      } else {
        const errorMessage = result.error || 'Failed to extract coordinates';
        setUrlError(errorMessage);
        toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
      }
    } catch (error) {
      const errorMessage = 'Error processing URL';
      setUrlError(errorMessage);
      toastHandler({ type: 'error', message: `❌ ${errorMessage}` });
    } finally {
      setIsExtracting(false);
    }
  };

  const validateForm = (): boolean => {
    if (!formData.addressName.trim()) {
      setError('Address name is required');
      return false;
    }
    if (!formData.buildingNumber.trim()) {
      setError('Building number is required');
      return false;
    }
    if (!formData.street.trim()) {
      setError('Street is required');
      return false;
    }
    if (!formData.zone.trim()) {
      setError('Zone is required');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateForm() || !order) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare shipping address data - only include fields that have values
      const shippingAddressData: any = {
        shippingAddress: {}
      };

      // Only include fields that have values (not empty strings)
      if (formData.addressName.trim()) {
        shippingAddressData.shippingAddress.addressName = formData.addressName.trim();
      }
      if (formData.buildingNumber.trim()) {
        shippingAddressData.shippingAddress.buildingNumber = formData.buildingNumber.trim();
      }
      if (formData.street.trim()) {
        shippingAddressData.shippingAddress.street = formData.street.trim();
      }
      if (formData.zone.trim()) {
        shippingAddressData.shippingAddress.zone = formData.zone.trim();
      }
      if (formData.latitude !== null && formData.latitude !== undefined) {
        shippingAddressData.shippingAddress.latitude = Number(formData.latitude);
      }
      if (formData.longitude !== null && formData.longitude !== undefined) {
        shippingAddressData.shippingAddress.longitude = Number(formData.longitude);
      }
      if (formData.addressType) {
        shippingAddressData.shippingAddress.addressType = formData.addressType;
      }

      console.log('Updating shipping address via new API:', shippingAddressData);
      const response = await ShippingAddress.patch(order.id, shippingAddressData);
      
      if (response && response.success) {
        // Update the order with the new shipping address
        const updatedOrder = {
          ...order,
          shippingAddress: {
            ...order.shippingAddress,
            ...response.shippingAddress,
          },
        };
        
        onAddressUpdate(updatedOrder);
        onClose();
        toastHandler({
          message: response.message || 'Shipping address updated successfully',
          type: 'success',
        });
      }
    } catch (error: any) {
      console.error('Error updating address:', error);
      setError(error.message || 'Failed to update address');
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Edit Delivery Address</Typography>
        <Typography variant="body2" color="text.secondary">
          Update the delivery address and location coordinates for this order
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set delivery location coordinates by clicking on the map, entering manually, or extracting from a Google Maps URL.
          </Typography>

          {/* Google Maps URL Input */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
              Extract from Google Maps URL
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label="Google Maps URL"
                placeholder="Paste Google Maps URL here"
                value={googleMapsUrl}
                onChange={handleUrlChange}
                error={!!urlError}
                helperText={urlError || "Supports full URLs and shortened URLs"}
                sx={{ flex: 1 }}
                disabled={isExtracting || loading}
              />
              <Button
                variant="contained"
                onClick={handleExtractFromUrl}
                disabled={!googleMapsUrl.trim() || isExtracting || loading}
                sx={{ minWidth: 120, ml: 1 }}
              >
                {isExtracting ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
                    Extracting...
                  </>
                ) : (
                  'Extract'
                )}
              </Button>
            </Box>
          </Box>
          
          {/* Interactive Map */}
          <Box sx={{ height: 300, width: '100%', mb: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <MapContainer
              center={[
                typeof formData.latitude === 'number' ? formData.latitude : 25.3548, 
                typeof formData.longitude === 'number' ? formData.longitude : 51.1839
              ]}
              zoom={13}
              style={{ height: '100%', width: '100%' }}
              attributionControl={false}
            >
              <TileLayer url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png' />
              <MapClickHandler onLocationSelect={handleMapClick} />
              {formData.latitude !== null && formData.longitude !== null && 
               typeof formData.latitude === 'number' && typeof formData.longitude === 'number' && (
                <Marker 
                  position={[formData.latitude, formData.longitude]}
                  icon={customIcon}
                />
              )}
            </MapContainer>
          </Box>

          {/* Address Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Name"
                value={formData.addressName}
                onChange={(e) => handleInputChange('addressName', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter address name"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Building Number"
                value={formData.buildingNumber}
                onChange={(e) => handleInputChange('buildingNumber', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter building number"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Street"
                value={formData.street}
                onChange={(e) => handleInputChange('street', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter street name"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Zone"
                value={formData.zone}
                onChange={(e) => handleInputChange('zone', e.target.value)}
                required
                disabled={loading}
                placeholder="Enter zone"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Address Type"
                value={formData.addressType}
                onChange={(e) => handleInputChange('addressType', e.target.value)}
                disabled={loading}
                SelectProps={{
                  native: true,
                }}
              >
                <option value="home">Home</option>
                <option value="office">Office</option>
                <option value="other">Other</option>
              </TextField>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Latitude"
                type="number"
                value={formData.latitude || ''}
                onChange={(e) => handleInputChange('latitude', e.target.value)}
                disabled={loading}
                inputProps={{
                  step: "any",
                  min: -90,
                  max: 90
                }}
                helperText="Range: -90 to 90"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Longitude"
                type="number"
                value={formData.longitude || ''}
                onChange={(e) => handleInputChange('longitude', e.target.value)}
                disabled={loading}
                inputProps={{
                  step: "any",
                  min: -180,
                  max: 180
                }}
                helperText="Range: -180 to 180"
              />
            </Grid>
          </Grid>

          {formData.latitude !== null && formData.longitude !== null && 
           typeof formData.latitude === 'number' && typeof formData.longitude === 'number' && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Selected Location:</strong> {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <a 
                  href={`https://www.google.com/maps?q=${formData.latitude},${formData.longitude}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleCancel} disabled={loading || isExtracting}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={loading || isExtracting}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Saving...' : 'Save Address'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddressEditDialog;

